import { useEffect, useState } from 'react';
import { ArrowLeftRight } from 'lucide-react';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import {
  distInputStrToMeters,
  effectiveDistanceDisplayUnit,
  estimatedDurationSecFromDistance,
  estimatePaceSecPerKm,
  formatDistanceFromMeters,
  formatSecToMss,
  formatSplitDurationLabel,
  metersForSegmentDistanceLine,
  metersToDistInputStr,
  normalizeDistanceDisplayUnit,
  parseMssToSec,
} from '../../utils/runningSegmentLabels';

/**
 * Split measure (duration vs distance), editable primary field, read-only estimate.
 * @param {object} props
 * @param {object} props.seg — segment row (localKey, duration_sec, distance_m, split_measure_mode)
 * @param {(p: object) => void} props.onPatch
 * @param {'km'|'miles'} props.distanceUnits — user Settings preference (default unit for new / reset)
 * @param {number|null|undefined} props.thresholdPaceSecPerKm
 * @param {{ cpWatts?: number|null, powerPaceModel?: object|null }} [props.paceEstimateOpts]
 * @param {(k: string, o?: object) => string} props.t
 * @param {'default'|'compact'} [props.density]
 * @param {boolean} [props.hideMeasureSelector] — table row: parent renders measure elsewhere
 * @param {'default'|'tableRow'} [props.layoutVariant] — tableRow: single horizontal line, narrow distance field
 * @param {() => void} [props.onFlipAllDistanceUnits] — swap km↔mi for all distance splits (graphic popover)
 * @param {import('react').ReactNode} [props.inlineActions] — e.g. duplicate/delete; shown right-aligned on the duration/distance row (default layout only)
 */
export default function SegmentMeasureEditorFields({
  seg,
  onPatch,
  distanceUnits,
  thresholdPaceSecPerKm,
  paceEstimateOpts,
  t,
  density = 'default',
  hideMeasureSelector = false,
  layoutVariant = 'default',
  onFlipAllDistanceUnits,
  inlineActions = null,
}) {
  const measureMode = seg.split_measure_mode === 'distance' ? 'distance' : 'duration';
  const userDef = distanceUnits === 'miles' ? 'miles' : 'km';

  const [durInput, setDurInput] = useState(() => formatSecToMss(seg.duration_sec));
  const [distInput, setDistInput] = useState('');

  /** Always from segment (+ Settings fallback) so bulk “swap all units” stays in sync with the Unit select. */
  const displayUnit = effectiveDistanceDisplayUnit(seg, userDef);

  useEffect(() => {
    const u = effectiveDistanceDisplayUnit(seg, userDef);
    setDistInput(metersToDistInputStr(seg.distance_m, u));
    setDurInput(formatSecToMss(seg.duration_sec));
  }, [seg.localKey, seg.distance_m, seg.distance_display_unit, seg.duration_sec, userDef]);

  const commitDuration = () => {
    const parsed = parseMssToSec(durInput);
    if (parsed == null) {
      setDurInput(formatSecToMss(seg.duration_sec));
      return;
    }
    onPatch({ duration_sec: parsed });
  };

  const commitDistance = () => {
    const m = distInputStrToMeters(distInput, displayUnit);
    if (m == null) {
      setDistInput(metersToDistInputStr(seg.distance_m, displayUnit));
      return;
    }
    onPatch({ distance_m: m, distance_display_unit: displayUnit });
  };

  const durStored = Number(seg.duration_sec) || 0;
  const durEstSec =
    measureMode === 'distance'
      ? estimatedDurationSecFromDistance(seg, thresholdPaceSecPerKm, paceEstimateOpts) ??
        (durStored > 0 ? durStored : null)
      : null;
  const durEstLabel =
    durEstSec != null && durEstSec > 0
      ? formatSplitDurationLabel(durEstSec, t)
      : t('runningExercise.graphicNoDuration');

  const mEst = metersForSegmentDistanceLine(seg, thresholdPaceSecPerKm, paceEstimateOpts);
  const distEstLabel =
    formatDistanceFromMeters(mEst, userDef, t) || t('runningExercise.graphicNoDuration');

  const labelClass = density === 'compact' ? 'text-[10px]' : 'text-xs';
  const inputClass = density === 'compact' ? 'h-8 text-xs' : 'h-8 text-xs';
  const estimateClass = density === 'compact' ? 'text-[10px]' : 'text-[10px]';

  const estimateBlock = (value, mono) => (
    <div className="flex min-w-0 flex-col justify-end gap-0 pb-0.5">
      <span className={`${estimateClass} leading-tight text-muted-foreground`}>
        {t('runningExercise.segmentMeasureEstimate')}
      </span>
      <span
        className={`${estimateClass} leading-tight text-foreground tabular-nums${mono ? ' font-mono' : ''}`}
      >
        {value}
      </span>
    </div>
  );

  /** One line, for default layout beside duration/distance inputs */
  const estimateInline = (value, mono) => (
    <span
      className={`${estimateClass} inline-flex min-w-0 flex-wrap items-baseline gap-x-1.5 leading-tight text-foreground`}
    >
      <span className="shrink-0 text-muted-foreground">{t('runningExercise.segmentMeasureEstimate')}</span>
      <span className={`tabular-nums${mono ? ' font-mono' : ''}`}>{value}</span>
    </span>
  );

  if (layoutVariant === 'tableRow' && hideMeasureSelector) {
    return (
      <div className="flex min-w-0 flex-nowrap items-end gap-2 text-card-foreground">
        {measureMode === 'duration' ? (
          <>
            <div className="w-[5.25rem] shrink-0 space-y-0.5">
              <Label className={labelClass}>{t('runningExercise.segDurationMss')}</Label>
              <Input
                className={`${inputClass} w-full font-mono tabular-nums`}
                value={durInput}
                onChange={(e) => setDurInput(e.target.value)}
                onBlur={commitDuration}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    commitDuration();
                  }
                }}
              />
            </div>
            {estimateBlock(distEstLabel, false)}
          </>
        ) : (
          <>
            <div className="w-[3.75rem] shrink-0 space-y-0.5">
              <Label className={labelClass}>{t('runningExercise.segDistanceValue')}</Label>
              <Input
                className={`${inputClass} w-full px-1.5 tabular-nums`}
                inputMode="decimal"
                value={distInput}
                onChange={(e) => setDistInput(e.target.value)}
                onBlur={commitDistance}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    commitDistance();
                  }
                }}
              />
            </div>
            <div className="w-[4.75rem] shrink-0 space-y-0.5">
              <Label className={labelClass}>{t('runningExercise.segDistanceUnit')}</Label>
              <Select
                value={displayUnit}
                onValueChange={(v) => {
                  const u = v === 'miles' ? 'miles' : 'km';
                  onPatch({ distance_display_unit: u });
                  const m = Number(seg.distance_m);
                  if (Number.isFinite(m) && m > 0) {
                    setDistInput(metersToDistInputStr(m, u));
                  }
                }}
              >
                <SelectTrigger className={`${inputClass} w-full px-2 text-xs`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="z-[10500]">
                  <SelectItem value="km">{t('runningExercise.unitsKm')}</SelectItem>
                  <SelectItem value="miles">{t('runningExercise.unitsMiles')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {estimateBlock(durEstLabel, true)}
          </>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3 text-popover-foreground">
      {!hideMeasureSelector && (
        <div className="space-y-1">
          <Label className={labelClass}>{t('runningExercise.splitMeasureMode')}</Label>
          <div className="flex items-end gap-2">
            <div className="min-w-0 flex-1">
              <Select
                value={measureMode}
                onValueChange={(v) => {
                  const next = v === 'distance' ? 'distance' : 'duration';
                  const pace = estimatePaceSecPerKm(seg, thresholdPaceSecPerKm, paceEstimateOpts);
                  const patch = { split_measure_mode: next };
                  if (next === 'distance') {
                    const dur = Number(seg.duration_sec) || 0;
                    if (dur > 0 && pace > 0) {
                      patch.distance_m = Math.round((dur / pace) * 1000);
                      patch.duration_sec = 0;
                    }
                  } else {
                    const dm = Number(seg.distance_m) || 0;
                    if (dm > 0 && pace > 0) {
                      patch.duration_sec = Math.round((dm / 1000) * pace);
                      patch.distance_m = 0;
                    }
                    patch.distance_display_unit = null;
                  }
                  onPatch(patch);
                }}
              >
                <SelectTrigger className={inputClass}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="z-[10500]">
                  <SelectItem value="duration">{t('runningExercise.splitMeasureDuration')}</SelectItem>
                  <SelectItem value="distance">{t('runningExercise.splitMeasureDistance')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {measureMode === 'distance' && typeof onFlipAllDistanceUnits === 'function' && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="h-8 w-8 shrink-0"
                    onClick={onFlipAllDistanceUnits}
                  >
                    <ArrowLeftRight className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="left" className="z-[10510] max-w-[260px] text-xs">
                  {t('runningExercise.flipDistanceUnitsTooltip')}
                </TooltipContent>
              </Tooltip>
            )}
          </div>
          <p className={`${estimateClass} text-muted-foreground`}>
            {t('runningExercise.splitMeasureHint', {
              units: userDef === 'miles' ? t('runningExercise.unitsMiles') : t('runningExercise.unitsKm'),
            })}
          </p>
          {!!normalizeDistanceDisplayUnit(seg.distance_display_unit) && (
            <p className={`${estimateClass} text-muted-foreground`}>{t('runningExercise.splitExerciseUnitFromPlan')}</p>
          )}
        </div>
      )}

      {measureMode === 'duration' ? (
        <div className="space-y-1">
          <Label className={labelClass}>{t('runningExercise.segDurationMss')}</Label>
          <div
            className={`flex flex-wrap items-center gap-x-3 gap-y-1${inlineActions ? ' justify-between' : ''}`}
          >
            <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
              <Input
                className={`${inputClass} w-full max-w-[9rem] shrink-0 font-mono tabular-nums`}
                value={durInput}
                onChange={(e) => setDurInput(e.target.value)}
                onBlur={commitDuration}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    commitDuration();
                  }
                }}
              />
              {estimateInline(distEstLabel, false)}
            </div>
            {inlineActions ? (
              <div className="flex shrink-0 items-center gap-1.5">{inlineActions}</div>
            ) : null}
          </div>
        </div>
      ) : (
        <div
          className={`flex flex-wrap items-end gap-3${inlineActions ? ' justify-between' : ''}`}
        >
          <div className="flex min-w-0 flex-1 flex-wrap items-end gap-3">
            <div className="grid min-w-0 grid-cols-[1fr_auto] gap-2">
              <div className="space-y-1">
                <Label className={labelClass}>{t('runningExercise.segDistanceValue')}</Label>
                <Input
                  className={`${inputClass} tabular-nums`}
                  inputMode="decimal"
                  value={distInput}
                  onChange={(e) => setDistInput(e.target.value)}
                  onBlur={commitDistance}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      commitDistance();
                    }
                  }}
                />
              </div>
              <div className="space-y-1">
                <Label className={labelClass}>{t('runningExercise.segDistanceUnit')}</Label>
                <Select
                  value={displayUnit}
                  onValueChange={(v) => {
                    const u = v === 'miles' ? 'miles' : 'km';
                    onPatch({ distance_display_unit: u });
                    const m = Number(seg.distance_m);
                    if (Number.isFinite(m) && m > 0) {
                      setDistInput(metersToDistInputStr(m, u));
                    }
                  }}
                >
                  <SelectTrigger className={`${inputClass} w-[5.5rem]`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="z-[10500]">
                    <SelectItem value="km">{t('runningExercise.unitsKm')}</SelectItem>
                    <SelectItem value="miles">{t('runningExercise.unitsMiles')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {estimateInline(durEstLabel, true)}
          </div>
          {inlineActions ? (
            <div className="flex shrink-0 items-center gap-1.5">{inlineActions}</div>
          ) : null}
        </div>
      )}
    </div>
  );
}
